import { useMemo } from 'react'

import { useMutation } from '@apollo/client'
import { IconSwitch3 } from '@tabler/icons-react'
import Image from 'next/image'

import {
  KeyValue,
  KeyValueDropdown,
} from './form-components/dropdowns/KeyValueDropdown'
import { Alert } from './shared-components/BUI/Alert'
import { Button } from './shared-components/BUI/Button'
import { Form } from './shared-components/BUI/Form'
import { Modal } from './shared-components/BUI/Modal'
import { Text } from './shared-components/BUI/Text'
import { Flexbox } from './shared-components/Flexbox'

import { useAuth } from '@/context/authContext'
import { useFilters } from '@/context/filterContext'
import { useProject } from '@/context/projectContext'
import { useUser } from '@/context/userContext'
import { SWITCH_BUILDER } from '@/modules/adminUsers/graphQL'

import bpLogo from 'public/buildpass-small.svg'

import {
  SwitchBuilderUiMutation,
  SwitchBuilderUiMutationVariables,
} from 'types/api.graphql'

enum Field {
  Org = 'org',
}

type FormFieldValues = {
  [Field.Org]: string
}

type Props = {
  open: boolean
  closeModal: () => void
}

const BuilderSelectModal: React.FC<Props> = ({ open, closeModal }) => {
  const { clearAllFilters } = useFilters()
  const { builder, userLoading, builders } = useUser()
  const { isLoaded, changeOrg, orgId, organisations } = useAuth()
  const { selectAllProjects } = useProject() ?? {}

  const orgsWithMatchingBuilder = useMemo(() => {
    if (!isLoaded || userLoading || !organisations?.length) return []

    return (
      organisations
        .filter((o) => builders.some((b) => b.clerkOrganizationId === o.id))
        .sort((a, b) => {
          if (orgId) {
            if (a.id === orgId) return -1
            if (b.id === orgId) return 1
          }
        }) ?? []
    )
  }, [builders, isLoaded, orgId, organisations, userLoading])

  const [switchBuilder, { loading }] = useMutation<
    SwitchBuilderUiMutation,
    SwitchBuilderUiMutationVariables
  >(SWITCH_BUILDER)

  const options = useMemo<Record<string, KeyValue>>(() => {
    if (!isLoaded || userLoading || !orgsWithMatchingBuilder?.length) return {}

    return orgsWithMatchingBuilder.reduce<Record<string, KeyValue>>(
      (acc, org) => {
        acc[org.id] = { label: org.name }
        return acc
      },
      {}
    )
  }, [isLoaded, orgsWithMatchingBuilder, userLoading])

  if (userLoading) return null

  const onSubmit = async (values: FormFieldValues) => {
    await switchBuilder({ variables: { orgId: values['org'] } })

    clearAllFilters()
    selectAllProjects?.()
    changeOrg(values['org'])
  }

  return (
    <Modal
      title="Switch company"
      open={open}
      onCancel={closeModal}
      modalRender={(children) => (
        <Form layout="vertical" onFinish={onSubmit}>
          {children}
        </Form>
      )}
      footerLayout="split"
      footer={
        <>
          <Button onClick={closeModal}>Cancel</Button>
          <Button
            type="submit"
            variant="primary"
            icon={<IconSwitch3 size={16} />}
            loading={loading}
          >
            Switch
          </Button>
        </>
      }
    >
      <Text strong mb="1rem">
        Current company
      </Text>

      <Flexbox alignItems="center" gap="0.5rem">
        <Image
          src={builder.logoUrl ?? bpLogo.src}
          alt={builder.company.name}
          width={40}
          height={24}
          style={{
            objectFit: 'contain',
            objectPosition: 'center',
            ...(!builder?.logoUrl && {
              transform: 'scale(0.6)',
              filter: 'grayscale(1) brightness(0.5)',
            }),
          }}
        />

        <Text>{builder.company.name}</Text>
      </Flexbox>

      <Text strong mt="1rem" mb="1rem">
        Switch to
      </Text>

      {!orgsWithMatchingBuilder?.length ? (
        <Alert
          variant="danger"
          title={
            <>
              No other companies found. Try logging out and back in. You should
              only need to do this once.
            </>
          }
        />
      ) : (
        <Form.Item
          name={Field.Org}
          rules={[
            {
              validator: (_, newOrgId) => {
                return orgId === newOrgId
                  ? Promise.reject('You are already on this builder')
                  : Promise.resolve()
              },
            },
          ]}
        >
          <KeyValueDropdown options={options} selectFirstByDefault showCaret />
        </Form.Item>
      )}
    </Modal>
  )
}

export default BuilderSelectModal
